import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    Autocomplete,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Grid,
    TextField,
    Typography,
    Alert,
    IconButton,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import {useQuery} from '@apollo/client';
import {SERVICES_QUERY} from '../../gql/appointments.gql';
import {STAFF_BY_UUID} from '../../gql/staff.gql';
import {useStaffManagementAPI} from '../../service/useStaffManagementAPI';
import {useReservationAdminAPI} from '../../service/useReservationAdminAPI';
import {useTranslation} from 'react-i18next';

const DAY_OF_WEEK = [1, 2, 3, 4, 5, 6, 7];

function labelForDay(t, idx) {
    return [t('monday'), t('tuesday'), t('wednesday'), t('thursday'), t('friday'), t('saturday'), t('sunday')][idx] || '';
}

function zeroPadTime(v) {
    if (!v) return '';
    const [h, m] = String(v).split(':');
    const hh = String(h ?? '00').padStart(2, '0');
    const mm = String(m ?? '00').padStart(2, '0');
    return `${hh}:${mm}`;
}

export default function StaffEditModal({open, onClose, initial, onSaved}) {
    const isEdit = !!(initial && initial.uuid);
    const {t} = useTranslation();

    const {data: servicesData, loading: servicesLoading, error: servicesError} = useQuery(SERVICES_QUERY, {variables: {active: true}});
    const services = servicesData?.services || [];
    const uuid = initial?.uuid;
    const {refetch} = useQuery(STAFF_BY_UUID, {
        variables: {uuid},
        fetchPolicy: 'network-only',
        skip: true,
    });

    const {upsertStaff} = useStaffManagementAPI();
    const {searchByCustomerName} = useReservationAdminAPI();
    const [errors, setErrors] = React.useState({});
    const [saving, setSaving] = React.useState(false);
    const [serverError, setServerError] = React.useState('');
    const [personOptions, setPersonOptions] = React.useState([]);
    const [personLoading, setPersonLoading] = React.useState(false);
    const searchDebounceRef = React.useRef(null);

    const makeEmptyForm = () => ({
        uuid: undefined,
        name: '',
        personUuid: undefined,
        active: true,
        serviceUuids: [],
        schedule: {
            workingHours: DAY_OF_WEEK.map((dow, idx) => ({
                dayOfWeek: dow,
                enabled: idx < 5,
                startTime: zeroPadTime('08:00'),
                endTime: zeroPadTime('17:00'),
                breaks: [],
            })),
        },
    });

    const makeFormFromInitial = (initial) => ({
        ...makeEmptyForm(),
        uuid: initial?.uuid,
        name:
            initial?.name ||
            `${initial?.person?.firstName || ''} ${initial?.person?.lastName || ''}`.trim(),
        personUuid: initial?.personUuid || initial?.person?.uuid || undefined,
        active: initial?.active ?? true,
        serviceUuids: Array.isArray(initial?.serviceUuids) ? initial.serviceUuids : [],
    });

    const [form, setForm] = React.useState(() => ({
        uuid: initial?.uuid,
        name: initial?.name || `${initial?.person?.firstName || ''} ${initial?.person?.lastName || ''}`.trim(),
        // Persist a link to an existing person if available (used by backend)
        personUuid: initial?.personUuid || initial?.person?.uuid || undefined,
        active: initial?.active ?? true,
        serviceUuids: Array.isArray(initial?.serviceUuids) ? initial.serviceUuids : [],
        schedule: {
            // New per-day working hours model
            workingHours: DAY_OF_WEEK.map((dow, idx) => ({
                dayOfWeek: dow,
                enabled: idx < 5, // Mon-Fri enabled by default
                startTime: zeroPadTime('08:00'),
                endTime: zeroPadTime('17:00'),
                breaks: [],
            }))
        },
    }));
    const [nameInput, setNameInput] = React.useState(form.name || '');

    React.useEffect(() => {
        if (!open) {
            const empty = makeEmptyForm();
            setForm(empty);
            setNameInput('');
            setErrors({});
            setServerError('');
            setPersonOptions([]);
            return;
        }

        // opening: set baseline immediately (so you don't see stale data flash)
        const base = makeFormFromInitial(initial);
        setForm(base);
        setNameInput(base.name);
    }, [open, initial]);

    React.useEffect(() => {
        if (!open || !uuid) return;

        refetch({ uuid }).then(({ data }) => {
            const staff = data?.staffByUuid;
            if (!staff) return;

            const computedName = `${staff?.person?.name || ''} ${staff?.person?.forename || ''}`.trim();

            const serviceUuids =
                staff?.staffServices?.map(ss => ss?.service?.uuid).filter(Boolean) ?? [];

            const existingWH = Array.isArray(staff?.workingHours) ? staff.workingHours : [];
            const whByDow = new Map(existingWH.map(wh => [Number(wh.dayOfWeek), wh]));

            const workingHours = DAY_OF_WEEK.map((dow) => {
                const wh = whByDow.get(dow);
                return wh
                    ? {
                        dayOfWeek: dow,
                        enabled: true,
                        startTime: zeroPadTime(wh.startTime || '08:00'),
                        endTime: zeroPadTime(wh.endTime || '17:00'),
                        breaks: (wh.breaks || []).map(b => ({
                            startTime: zeroPadTime(b.startTime),
                            endTime: zeroPadTime(b.endTime)
                        }))
                    }
                    : { dayOfWeek: dow, enabled: false, startTime: zeroPadTime('08:00'), endTime: zeroPadTime('17:00'), breaks: [] };
            });

            setForm((prev) => ({
                ...prev,
                uuid: staff.uuid,
                name: computedName || prev.name,
                personUuid: staff?.person?.uuid ?? prev.personUuid,
                active: staff?.active ?? prev.active,
                serviceUuids,
                schedule: { workingHours },
            }));

            setNameInput(computedName);
        });
    }, [open, uuid, refetch]);

    React.useEffect(() => {
        setNameInput(form.name || '');
    }, [form.name]);

    const runSearch = React.useCallback(async (nameInput) => {
        if (!nameInput || nameInput.trim().length < 2) {
            setPersonOptions([]);
            return;
        }
        try {
            setPersonLoading(true);
            const result = await searchByCustomerName(nameInput.trim());
            const personOptions = Array.isArray(result)
                ? result.map((p) => {
                    // Prefer forename and name if provided by backend; fall back to first/last names or a single "name" field
                    const first = p?.forename || p?.person?.forename || p?.firstName || p?.person?.firstName || '';
                    const last = p?.name || p?.person?.name || p?.lastName || p?.person?.lastName || '';
                    const combined = `${first} ${last}`.trim();
                    const label = combined || p?.label || p?.displayName || p?.fullName || p?.name || '';
                    // Normalize a personUuid we can send to backend
                    const personUuid = p?.uuid || p?.personUuid || p?.id || p?.person?.uuid || undefined;
                    return {...p, label, personUuid};
                })
                : [];
            setPersonOptions(personOptions);
        } finally {
            setPersonLoading(false);
        }
    }, [searchByCustomerName]);

    React.useEffect(() => {
        // debounce input changes
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => runSearch(nameInput), 250);
        return () => {
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nameInput]);

    const toggleService = (uuid) => {
        setForm((f) => {
            const set = new Set(f.serviceUuids);
            if (set.has(uuid)) set.delete(uuid); else set.add(uuid);
            return {...f, serviceUuids: Array.from(set)};
        });
    };

    const toggleDayEnabled = (idx) => {
        setForm((f) => {
            const arr = [...(f.schedule.workingHours || [])];
            arr[idx] = {...arr[idx], enabled: !arr[idx].enabled};
            return {...f, schedule: {...f.schedule, workingHours: arr}};
        });
    };

    const onChangeWorkingHour = (idx, field, value) => {
        setForm((f) => {
            const arr = [...(f.schedule.workingHours || [])];
            const v = field.includes('Time') ? zeroPadTime(value) : value;
            arr[idx] = {...arr[idx], [field]: v};
            return {...f, schedule: {...f.schedule, workingHours: arr}};
        });
    };

    const addBreak = (dayIdx) => {
        setForm((f) => {
            const arr = [...(f.schedule.workingHours || [])];
            const day = {...arr[dayIdx]};
            day.breaks = [...(day.breaks || []), {startTime: '12:00', endTime: '13:00'}];
            arr[dayIdx] = day;
            return {...f, schedule: {...f.schedule, workingHours: arr}};
        });
    };

    const removeBreak = (dayIdx, breakIdx) => {
        setForm((f) => {
            const arr = [...(f.schedule.workingHours || [])];
            const day = {...arr[dayIdx]};
            day.breaks = day.breaks.filter((_, i) => i !== breakIdx);
            arr[dayIdx] = day;
            return {...f, schedule: {...f.schedule, workingHours: arr}};
        });
    };

    const onChangeBreak = (dayIdx, breakIdx, field, value) => {
        setForm((f) => {
            const arr = [...(f.schedule.workingHours || [])];
            const day = {...arr[dayIdx]};
            const breaks = [...(day.breaks || [])];
            breaks[breakIdx] = {...breaks[breakIdx], [field]: zeroPadTime(value)};
            day.breaks = breaks;
            arr[dayIdx] = day;
            return {...f, schedule: {...f.schedule, workingHours: arr}};
        });
    };

    const validate = () => {
        const v = {};
        const name = (form.name || '').trim();
        if (!name) v.name = t('nameRequired');
        if (!form.serviceUuids || form.serviceUuids.length === 0) v.serviceUuids = t('selectAtLeastOneService');
        const wh = form.schedule?.workingHours || [];
        const enabled = wh.filter((d) => d.enabled);
        if (enabled.length === 0) v.days = t('selectAtLeastOneDay');
        // per-day validation
        const timeErrorFor = (s, e) => {
            if (!s || !e) return t('startEndRequired');
            const [sh, sm] = s.split(':').map(Number);
            const [eh, em] = e.split(':').map(Number);
            const startM = sh * 60 + sm;
            const endM = eh * 60 + em;
            return startM < endM ? '' : t('startBeforeEnd');
        };
        for (const d of enabled) {
            const err = timeErrorFor(d.startTime, d.endTime);
            if (err) {
                v.time = err;
                break;
            }
            for (const b of (d.breaks || [])) {
                const bErr = timeErrorFor(b.startTime, b.endTime);
                if (bErr) {
                    v.time = bErr;
                    break;
                }
            }
            if (v.time) break;
        }
        setErrors(v);
        return Object.keys(v).length === 0;
    };

    const onSave = async () => {
        setServerError('');
        if (!validate()) return;
        const payload = {
            uuid: form.uuid,
            name: form.name.trim(),
            // Include a link to an existing person when provided by suggestions
            ...(form.personUuid ? {personUuid: form.personUuid} : {}),
            active: !!form.active,
            serviceUuids: form.serviceUuids,
            workingHours: (form.schedule.workingHours || [])
                .filter((d) => d.enabled)
                .map((d) => ({
                    dayOfWeek: d.dayOfWeek,
                    startTime: zeroPadTime(d.startTime),
                    endTime: zeroPadTime(d.endTime),
                    breaks: (d.breaks || []).map(b => ({
                        startTime: zeroPadTime(b.startTime),
                        endTime: zeroPadTime(b.endTime)
                    }))
                })),
        };
        try {
            setSaving(true);
            await upsertStaff(payload);
            onSaved?.();
            onClose?.();
        } catch (e) {
            setServerError(t('saveFailedTryAgain'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isEdit ? t('editStaffTitle') : t('newStaffTitle')}</DialogTitle>
            <DialogContent>
                <Box sx={{mt: 1}}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                            <Autocomplete
                                freeSolo
                                loading={personLoading}
                                options={personOptions}
                                getOptionLabel={(opt) => (typeof opt === 'string' ? opt : (opt?.label || ''))}
                                filterOptions={(x) => x}
                                value={form.name || ''}
                                onChange={(_e, newValue) => {
                                    // newValue can be string (freeSolo) or option object
                                    const nameVal = typeof newValue === 'string' ? newValue : (newValue?.label || '');
                                    const pickedPersonUuid =
                                        typeof newValue === 'string'
                                            ? undefined
                                            : (newValue?.personUuid || newValue?.uuid || newValue?.id || newValue?.person?.uuid || undefined);
                                    setForm((f) => ({...f, name: nameVal, personUuid: pickedPersonUuid}));
                                }}
                                inputValue={nameInput}
                                onInputChange={(_e, newInput) => {
                                    setNameInput(newInput);
                                    // also update the form name so validation reflects typed value
                                    // when the user types free text, clear any previously selected personUuid
                                    setForm((f) => ({...f, name: newInput, personUuid: undefined}));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('name')}
                                        fullWidth
                                        required
                                        error={!!errors.name}
                                        helperText={errors.name || t('typeToSearchPersons')}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControlLabel
                                control={<Checkbox checked={!!form.active} onChange={(e) => setForm((f) => ({...f, active: e.target.checked}))}/>}
                                label={t('active')}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight={600}>{t('canPerformServices')}</Typography>
                            {servicesLoading && <Typography variant="body2">{t('loadingServices')}</Typography>}
                            {servicesError && <Alert severity="error">{t('servicesLoadFailed')}</Alert>}
                            {!servicesLoading && !servicesError && (
                                <FormGroup>
                                    {services.map((s) => (
                                        <FormControlLabel
                                            key={s.uuid}
                                            control={<Checkbox checked={form?.serviceUuids?.includes(s.uuid)} onChange={() => toggleService(s.uuid)}/>}
                                            label={s.name}
                                        />
                                    ))}
                                </FormGroup>
                            )}
                            {errors.serviceUuids && (
                                <Box sx={{color: 'error.main', fontSize: 12, mt: 0.5}}>{errors.serviceUuids}</Box>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight={600}>{t('planning')}</Typography>
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                                {(form.schedule?.workingHours || []).map((d, idx) => (
                                    <React.Fragment key={d.dayOfWeek}>
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item xs={12} sm={4} md={3}>
                                                <FormControlLabel
                                                    control={<Checkbox checked={!!d.enabled} onChange={() => toggleDayEnabled(idx)}/>}
                                                    label={labelForDay(t, idx)}
                                                />
                                            </Grid>
                                            <Grid item xs={6} sm={4} md={3}>
                                                <TextField
                                                    type="time"
                                                    label={t('startTime')}
                                                    value={d.startTime}
                                                    onChange={(e) => onChangeWorkingHour(idx, 'startTime', e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                    disabled={!d.enabled}
                                                    InputLabelProps={{shrink: true}}
                                                    error={!!errors.time && d.enabled}
                                                />
                                            </Grid>
                                            <Grid item xs={6} sm={3} md={3}>
                                                <TextField
                                                    type="time"
                                                    label={t('endTime')}
                                                    value={d.endTime}
                                                    onChange={(e) => onChangeWorkingHour(idx, 'endTime', e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                    disabled={!d.enabled}
                                                    InputLabelProps={{shrink: true}}
                                                    error={!!errors.time && d.enabled}
                                                    helperText={idx === 0 ? errors.time : ''}
                                                />
                                            </Grid>
                                            <Grid item xs={1}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    disabled={!d.enabled}
                                                    onClick={() => addBreak(idx)}
                                                    title={t('addBreak')}
                                                >
                                                    <AddIcon fontSize="small" />
                                                    {t('addBreak')}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                        {d.enabled && (d.breaks || []).map((b, bIdx) => (
                                            <Grid key={bIdx} container spacing={1} alignItems="center" sx={{ mb: 1, width: 'auto' }}>
                                                <Grid item xs={6} sm={4} md={3}/>
                                                <Grid item xs={6} sm={3} md={3}>
                                                    <TextField
                                                        type="time"
                                                        label={t('breakStart')}
                                                        value={b.startTime}
                                                        onChange={(e) => onChangeBreak(idx, bIdx, 'startTime', e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                </Grid>
                                                <Grid item xs={5} sm={4} md={3}>
                                                    <TextField
                                                        type="time"
                                                        label={t('breakEnd')}
                                                        value={b.endTime}
                                                        onChange={(e) => onChangeBreak(idx, bIdx, 'endTime', e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeBreak(idx, bIdx)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </Box>
                            {errors.days && (
                                <Box sx={{color: 'error.main', fontSize: 12, mt: 0.5}}>{errors.days}</Box>
                            )}
                        </Grid>

                        {serverError && (
                            <Grid item xs={12}>
                                <Box sx={{color: 'error.main', fontSize: 14}}>{serverError}</Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving} data-testid="cancel-staff-button">{t('cancel')}</Button>
                <Button onClick={onSave} variant="contained" disabled={saving} data-testid="save-staff-button">{t('save')}</Button>
            </DialogActions>
        </Dialog>
    );
}
